// @ts-nocheck - Implicit any parameters and unused variables
import dayjs from 'dayjs';
import {
  Cell,
  Color,
  DynamicTable,
  Icons,
  IconWrapper,
  LinkAnimatedUnderline,
  RddkTheme,
  TableData,
  Tooltip,
} from 'db-npm-e4sc-rd-ui';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { OpenOffer } from './types';

type TableClosedBondsProps = {
  data: OpenOffer[];
  handleClick: any;
};

export const TableClosedBonds: React.FC<TableClosedBondsProps> = ({ data, handleClick }) => {
  const { t } = useTranslation();
  const formatDate = (date: string) => {
    //extract and format data from "dd-mm-yyyy 00:00:00" to "yyyy.mm.dd"
    let datearray = date.slice(0, 10).split('-');
    let newdate = datearray[2] + '-' + datearray[1] + '-' + datearray[0];
    return dayjs(newdate).format('DD.MM.YYYY');
  };

  const tableData = React.useMemo((): TableData => {
    return [
      // Header row:
      [
        {
          style: {
            width: 250,
          },
          value: t(`kurser-closedbonds-header`),
          alignVertically: 'middle',
          minWidth: 170,
        },
        // This column is here match "show details" column so sorting works (sorting functions uses the index of the header as the indes for the data column)
        {
          style: {
            borderLeft: 0,
            width: 120,
          },
          value: null,
          alignVertically: 'middle',
        },
        {
          style: {
            width: 250,
          },
          value: t(`kurser-closedbonds-columnname-maturity`),
          sortable: true,

          alignVertically: 'middle',
          minWidth: 170,
        },
        {
          style: {
            width: 250,
          },
          value: t(`kurser-closedbonds-columnname-interestRate`),
          sortable: true,

          alignVertically: 'middle',
          minWidth: 170,
        },
        {
          style: {
            width: 210,
          },
          value: t(`kurser-closedbonds-columnname-redemptionPrice`),
          alignVertically: 'middle',
          minWidth: 140,
          sortable: false,
        },
        {
          style: {
            width: 20,
          },
          value: (
            <Tooltip
              element={
                <IconWrapper size="24" color="Action">
                  <Icons.Action.Question />
                </IconWrapper>
              }
              content={t('kurser-closedbonds-tooltip-Kursvalue-text')}
            />
          ),
          alignVertically: 'middle',
        },
        {
          style: {
            width: 250,
          },
          value: t(`kurser-closedbonds-columnname-redemptionDate`),
          alignVertically: 'middle',
          minWidth: 170,
        },
      ],
      // Body rows:
      ...(data?.length ? data : [null]).map((item): Cell[] => {
        if (!item) {
          //show dummy row if no data is available to avoid table collapse
          return [
            { style: { width: 250 }, minWidth: 170, value: '-', alignVertically: 'middle' },
            //second column is here to match "show details" column so sorting works (sorting functions uses the index of the header as the index for the data column)
            {
              style: { border: '1px solid rgba(0, 0, 0, 0)', width: 250 },
              minWidth: 170,
              value: '',
              alignVertically: 'middle',
            },
            { style: { width: 250 }, minWidth: 170, value: '-', alignVertically: 'middle', align: 'center' },
            { style: { width: 210 }, minWidth: 140, value: '-', alignVertically: 'middle' },
            { style: { width: 210 }, minWidth: 140, value: '-', alignVertically: 'middle' },
            //sixth column is here to match "tooltip" column so sorting works (sorting functions uses the index of the header as the index for the data column)
            { style: { border: '1px solid rgba(0, 0, 0, 0)', width: 20 }, value: '', alignVertically: 'middle' },
            { style: { width: 250 }, minWidth: 170, value: '-', alignVertically: 'middle' },
          ];
        }

        const closedBondPriceDate = item?.redemptionDate && formatDate(item?.redemptionDate);
        return [
          {
            style: {
              width: 250,
            },
            prefix:
              item?.bondname.slice(0, 1) === '-'
                ? item?.numberOfTermsWithoutRepayment
                  ? parseFloat(item.numberOfTermsWithoutRepayment) === 0
                    ? t('kurser-termsWithoutRepaymentEQ0-label') + `  (årgang ${item?.bondname.slice(15, 19)})`
                    : parseFloat(item.numberOfTermsWithoutRepayment) === 40
                    ? t('kurser-termsWithoutRepaymentEQ40-label') + `  (årgang ${item?.bondname.slice(15, 19)})`
                    : t('kurser-termsWithoutRepaymentGT40-label') + `  (årgang ${item?.bondname.slice(15, 19)})`
                  : ''
                : item?.numberOfTermsWithoutRepayment
                ? parseFloat(item.numberOfTermsWithoutRepayment) === 0
                  ? t('kurser-termsWithoutRepaymentEQ0-label') + `  (årgang ${item?.bondname.slice(14, 18)})`
                  : parseFloat(item.numberOfTermsWithoutRepayment) === 40
                  ? t('kurser-termsWithoutRepaymentEQ40-label') + `  (årgang ${item?.bondname.slice(14, 18)})`
                  : t('kurser-termsWithoutRepaymentGT40-label') + `  (årgang ${item?.bondname.slice(14, 18)})`
                : '',

            value: '',
            suffix: item?.isinCode ? <IsinSuffix>{item.isinCode}</IsinSuffix> : '',
          },
          {
            style: {
              borderLeft: 0,
              minWidth: 110,
              position: 'relative',
            },
            value: (
              <>
                {null}
                <Link
                  href={void 0}
                  textColor={Color.red50}
                  underlineColorPrimary={Color.red50}
                  onClick={(e) => {
                    handleClick(item?.isinCode, item?.bondname);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleClick(item?.isinCode, item?.bondname);
                  }}
                  aria-label={t('kurser-showLoanDetails-label')}
                  tabIndex={0}
                >
                  {t('kurser-showLoanDetails-label')}
                </Link>
              </>
            ),
            alignVertically: 'top',
          },
          {
            value:
              item?.termToMaturityYears === '' || item?.termToMaturityYears === undefined
                ? '-'
                : parseFloat(item.termToMaturityYears),
            alignVertically: 'middle',
          },
          {
            value:
              item?.nominelInterestRate === '' || item?.nominelInterestRate === undefined
                ? '-'
                : isNaN(Number(item?.nominelInterestRate))
                ? `${item?.nominelInterestRate}%`
                : `${item?.nominelInterestRate.replace('.', ',').slice(0, -3)}%`,
            alignVertically: 'middle',
          },
          {
            value:
              item?.prices?.[0]?.price === '' || item?.prices?.[0]?.price === undefined
                ? '-'
                : String(item?.prices?.[0]?.price).slice(0, -1),
            alignVertically: 'middle',
          },
          {
            style: {
              borderLeft: 0,
              maxWidth: '1px',
            },
            value: null,
          },
          {
            value: `${closedBondPriceDate === '' || closedBondPriceDate === 'undefined' ? '-' : closedBondPriceDate}`,
            alignVertically: 'middle',
          },
        ];
      }),
    ];
  }, [data, t, handleClick]);

  return <DynamicTable striped={false} bordered={true} firstRowIsHead={true} data={tableData} />;
};

const Link = styled(LinkAnimatedUnderline)`
  cursor: pointer;
  position: absolute;
  bottom: 21%;
  right: 19%;
  font-size: 12px;
  color: ${RddkTheme.colors.red50};
`;
const IsinSuffix = styled.span`
  && {
    color: #111; // Nearly black for maximum contrast on white
    font-weight: 700;
    opacity: 1;
  }
`;
